import type { Camp, CellKind } from '@orbomb/content';
import type { SimConfig } from './config';
import type { Band, CrossGeometry, CrossReach } from './types';

export function cellKind(config: SimConfig, col: number, row: number): CellKind | 'OUT' {
  if (col < 0 || row < 0 || col >= config.columns || row >= config.rows) return 'OUT';
  return config.map.cells[row * config.columns + col] ?? 'OUT';
}

export function isFreeCell(config: SimConfig, col: number, row: number): boolean {
  return cellKind(config, col, row) === 'EMPTY';
}

/**
 * Epicentre rule: the free cell nearest to the ball centre.
 * Normally the cell containing the centre; otherwise the free cell whose centre is
 * closest, ties broken by row then column.
 */
export function nearestFreeCell(
  config: SimConfig,
  x: number,
  y: number,
): { col: number; row: number } {
  const col = Math.min(config.columns - 1, Math.max(0, Math.floor(x)));
  const row = Math.min(config.rows - 1, Math.max(0, Math.floor(y)));
  if (isFreeCell(config, col, row)) return { col, row };
  let best = { col, row };
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let r = 0; r < config.rows; r++) {
    for (let c = 0; c < config.columns; c++) {
      if (!isFreeCell(config, c, r)) continue;
      const dx = c + 0.5 - x;
      const dy = r + 0.5 - y;
      const d = dx * dx + dy * dy;
      if (d < bestDistance) {
        bestDistance = d;
        best = { col: c, row: r };
      }
    }
  }
  return best;
}

const DIRECTIONS: readonly { key: keyof CrossReach; dc: number; dr: number }[] = [
  { key: 'left', dc: -1, dr: 0 },
  { key: 'right', dc: 1, dr: 0 },
  { key: 'up', dc: 0, dr: -1 },
  { key: 'down', dc: 0, dr: 1 },
];

/**
 * Cross geometry: four branches of `range` cells. A branch stops at
 * the boundary, stops *before* a block, and includes a base cell then stops.
 */
export function computeCross(
  config: SimConfig,
  col: number,
  row: number,
  range: number,
): CrossGeometry {
  const reach: CrossReach = { left: 0, right: 0, up: 0, down: 0 };
  const basesHit: Camp[] = [];
  for (const dir of DIRECTIONS) {
    for (let k = 1; k <= range; k++) {
      const c = col + dir.dc * k;
      const r = row + dir.dr * k;
      const kind = cellKind(config, c, r);
      if (kind === 'OUT' || kind === 'BLOCK') break;
      reach[dir.key] = k;
      if (kind === 'BASE') {
        const owner = config.map.baseOwner[r * config.columns + c] ?? null;
        if (owner && !basesHit.includes(owner)) basesHit.push(owner);
        break;
      }
    }
  }
  basesHit.sort((a, b) => (a === b ? 0 : a === 'ORANGE' ? -1 : 1));
  const hw = config.explosion.halfWidth;
  const horizontal: Band = {
    x0: col - reach.left,
    x1: col + 1 + reach.right,
    y0: row + 0.5 - hw,
    y1: row + 0.5 + hw,
  };
  const vertical: Band = {
    x0: col + 0.5 - hw,
    x1: col + 0.5 + hw,
    y0: row - reach.up,
    y1: row + 1 + reach.down,
  };
  return { col, row, reach, horizontal, vertical, basesHit };
}

export function inBand(band: Band, x: number, y: number): boolean {
  return x >= band.x0 && x <= band.x1 && y >= band.y0 && y <= band.y1;
}

/** A target is hit when its centre lies within the lethal area. */
export function inCross(cross: CrossGeometry, x: number, y: number): boolean {
  return inBand(cross.horizontal, x, y) || inBand(cross.vertical, x, y);
}
